import React, { useState } from "react";
import { 
  FiPlus, 
  FiCheckCircle, 
  FiActivity, 
  FiSliders, 
  FiLayers, 
  FiGitBranch,
  FiZap,
  FiHelpCircle
} from "react-icons/fi";
import type { 
  FtaAnalysis, 
  FtaNode, 
  FtaCutSet 
} from "../../../../types/rca.types.js";

export type FaultTreeWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  readOnly?: boolean;
  onSave?: (analysis: FtaAnalysis) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const INITIAL_CUT_SETS: FtaCutSet[] = [
  { id: "cs-1", cutSetNumber: 1, order: 1, eventLabels: ["Power Supply Surge"], calculatedProbability: 0.000800, criticalityRank: 1 },
  { id: "cs-2", cutSetNumber: 2, order: 2, eventLabels: ["Sensor A Calibration Drift", "Sensor B Feedback Loss"], calculatedProbability: 0.001345, criticalityRank: 2 },
  { id: "cs-3", cutSetNumber: 3, order: 1, eventLabels: ["Operator Override Error"], calculatedProbability: 0.000040, criticalityRank: 3 },
];

export function FaultTreeWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onSave,
  onRootCauseIdentified,
}: FaultTreeWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"tree" | "cutsets" | "calc">("tree");
  const [analysisType, setAnalysisType] = useState<"QUALITATIVE" | "QUANTITATIVE" | "HYBRID">("QUANTITATIVE");
  const [probPower, setProbPower] = useState<number>(0.0008);
  const [probSensorA, setProbSensorA] = useState<number>(0.035);
  const [probSensorB, setProbSensorB] = useState<number>(0.0384);

  // Calculated probabilities:
  // Sensor Failure (AND gate) = probSensorA * probSensorB
  const probSensorAnd = probSensorA * probSensorB;
  // System Failure (OR gate) = probPower + probSensorAnd - (probPower * probSensorAnd)
  const probSystemTop = probPower + probSensorAnd - (probPower * probSensorAnd);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            FTA
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              13. FTA Analysis Workspace (Fault Tree)
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">
                Boolean Logic & Minimal Cut Sets
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Deductive top-down failure analysis using Boolean logic gates to determine the probability of system-level failures.
            </p>
          </div>
        </div>

        {/* Subtabs */}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("tree")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "tree" 
                  ? "bg-white text-cyan-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Tree Hierarchy
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("cutsets")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "cutsets" 
                  ? "bg-white text-cyan-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Cut Sets (3)
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("calc")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "calc" 
                  ? "bg-white text-cyan-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Calculations
            </button>
          </div>
        </div>
      </div>

      {/* Probability Summary Strip */}
      <div className="px-6 py-3 bg-cyan-50/50 border-b border-cyan-100 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-slate-500">Top Event Probability: </span>
            <span className="font-mono font-bold text-cyan-800 text-sm">
              P(Top) = {probSystemTop.toFixed(6)}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Analysis Mode: </span>
            <span className="font-semibold text-slate-800">{analysisType}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            onRootCauseIdentified?.("Sensor A Calibration Drift combined with Sensor B Feedback Loss", "MEASUREMENT", 92);
          }}
          className="px-3.5 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold text-xs shadow-sm transition-colors"
        >
          Confirm Primary Contributor
        </button>
      </div>

      {/* Tree Visualization */}
      <div className="p-6">
        {activeTab === "tree" ? (
          <div className="bg-slate-50/70 rounded-xl border border-slate-200 p-6 flex flex-col items-center justify-center min-w-[700px] overflow-x-auto">
            {/* Top Event Node */}
            <div className="bg-rose-600 text-white px-6 py-3.5 rounded-xl shadow-md text-center max-w-sm border-2 border-rose-400">
              <div className="text-[10px] uppercase font-bold tracking-wider text-rose-200">TOP EVENT</div>
              <div className="text-xs font-bold mt-0.5">{problemStatement || "System Failure / Dimension Deviation"}</div>
              <div className="text-[11px] font-mono mt-1 text-rose-100">P = {probSystemTop.toFixed(6)}</div>
            </div>

            {/* Vertical Line to OR Gate */}
            <div className="w-0.5 h-6 bg-slate-400"></div>

            {/* OR Gate Badge */}
            <div className="px-3 py-1 bg-amber-500 text-white text-[11px] font-bold rounded-md shadow-sm flex items-center gap-1">
              <FiGitBranch className="w-3 h-3" /> OR GATE
            </div>

            {/* Vertical Line splitting to 2 branches */}
            <div className="w-0.5 h-6 bg-slate-400"></div>

            {/* Horizontal Branching Bar */}
            <div className="w-96 h-0.5 bg-slate-400 relative">
              <div className="absolute -top-1 left-0 w-2 h-2 rounded-full bg-slate-500"></div>
              <div className="absolute -top-1 right-0 w-2 h-2 rounded-full bg-slate-500"></div>
            </div>

            {/* Level 2 Nodes (Power Failure & Sensor Failure) */}
            <div className="w-96 flex justify-between pt-4">
              {/* Left Branch: Basic Event (Power Failure) */}
              <div className="flex flex-col items-center">
                <div className="w-0.5 h-4 bg-slate-400"></div>
                <div className="bg-white border-2 border-slate-300 p-3 rounded-xl shadow-sm text-center w-40">
                  <div className="text-[10px] text-slate-400 uppercase font-semibold">BASIC EVENT</div>
                  <div className="text-xs font-bold text-slate-800 mt-0.5">Power Failure</div>
                  <div className="mt-1.5 flex items-center justify-center gap-1">
                    <span className="text-[10px] text-slate-500">P:</span>
                    <input
                      type="number"
                      step="0.0001"
                      value={probPower}
                      disabled={readOnly}
                      onChange={(e) => setProbPower(Number(e.target.value))}
                      className="w-16 text-[11px] p-0.5 text-center font-mono border border-slate-300 rounded bg-white text-slate-900"
                    />
                  </div>
                </div>
              </div>

              {/* Right Branch: Intermediate Event (Sensor Failure) with AND Gate */}
              <div className="flex flex-col items-center">
                <div className="w-0.5 h-4 bg-slate-400"></div>
                <div className="bg-cyan-50 border-2 border-cyan-300 p-3 rounded-xl shadow-sm text-center w-44">
                  <div className="text-[10px] text-cyan-600 uppercase font-semibold">INTERMEDIATE EVENT</div>
                  <div className="text-xs font-bold text-cyan-900 mt-0.5">Sensor Subsystem</div>
                  <div className="text-[10px] font-mono text-cyan-700 mt-1">
                    P = {probSensorAnd.toFixed(6)}
                  </div>
                </div>

                {/* Vertical Line to AND Gate */}
                <div className="w-0.5 h-4 bg-slate-400"></div>
                <div className="px-2.5 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded shadow-sm">
                  AND GATE
                </div>
                <div className="w-0.5 h-4 bg-slate-400"></div>

                {/* Sub-branch bar */}
                <div className="w-56 h-0.5 bg-slate-400 relative">
                  <div className="absolute -top-1 left-0 w-2 h-2 rounded-full bg-slate-500"></div>
                  <div className="absolute -top-1 right-0 w-2 h-2 rounded-full bg-slate-500"></div>
                </div>

                {/* Basic Events: Sensor A & Sensor B */}
                <div className="w-56 flex justify-between pt-3">
                  <div className="bg-white border border-slate-300 p-2 rounded-lg text-center w-24 shadow-sm">
                    <div className="text-[9px] text-slate-400 font-semibold">BASIC EVENT</div>
                    <div className="text-[11px] font-bold text-slate-800">Sensor A Drift</div>
                    <input
                      type="number"
                      step="0.001"
                      value={probSensorA}
                      disabled={readOnly}
                      onChange={(e) => setProbSensorA(Number(e.target.value))}
                      className="w-full text-[10px] p-0.5 text-center font-mono border border-slate-300 rounded mt-1 bg-white text-slate-900"
                    />
                  </div>

                  <div className="bg-white border border-slate-300 p-2 rounded-lg text-center w-24 shadow-sm">
                    <div className="text-[9px] text-slate-400 font-semibold">BASIC EVENT</div>
                    <div className="text-[11px] font-bold text-slate-800">Sensor B Loss</div>
                    <input
                      type="number"
                      step="0.001"
                      value={probSensorB}
                      disabled={readOnly}
                      onChange={(e) => setProbSensorB(Number(e.target.value))}
                      className="w-full text-[10px] p-0.5 text-center font-mono border border-slate-300 rounded mt-1 bg-white text-slate-900"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === "cutsets" ? (
          /* Minimal Cut Sets Table */
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Cut Set #</th>
                  <th className="py-2.5 px-3">Order</th>
                  <th className="py-2.5 px-3">Contributing Basic Events</th>
                  <th className="py-2.5 px-3">Calculated Probability</th>
                  <th className="py-2.5 px-3">Criticality Rank</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {INITIAL_CUT_SETS.map(cs => (
                  <tr key={cs.id} className="hover:bg-slate-50">
                    <td className="py-2.5 px-3 font-bold text-cyan-700">Cut Set #{cs.cutSetNumber}</td>
                    <td className="py-2.5 px-3 font-medium">Order {cs.order}</td>
                    <td className="py-2.5 px-3 font-medium text-slate-900">
                      {cs.eventLabels.join(" AND ")}
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-slate-800">
                      {cs.calculatedProbability.toFixed(6)}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-cyan-50 text-cyan-700 border border-cyan-200">
                        Rank {cs.criticalityRank}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Calculations View */
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 text-xs">
            <h4 className="font-bold text-slate-900">Boolean Logic & Probability Formulations</h4>
            <div className="p-3 bg-white rounded-lg border border-slate-200 font-mono space-y-1 text-slate-800">
              <div>P(Sensor Subsystem) = P(Sensor A) × P(Sensor B) = {probSensorA} × {probSensorB} = {probSensorAnd.toFixed(6)}</div>
              <div>P(Top Event) = P(Power Failure) ∪ P(Sensor Subsystem) = {probSystemTop.toFixed(6)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
